import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Gavel, MapPin, Clock, TrendingUp, Maximize, BedDouble, Flame, Building2, IndianRupee, KeyRound } from "lucide-react";
import { api, formatINR, fileUrl } from "../lib/api";

function useCountdown(targetIso) {
    const [now, setNow] = useState(Date.now());
    useEffect(() => {
        const t = setInterval(() => setNow(Date.now()), 1000);
        return () => clearInterval(t);
    }, []);
    const diff = new Date(targetIso).getTime() - now;
    if (diff <= 0) return { ended: true, label: "Ended" };
    const d = Math.floor(diff / 86400000);
    const h = Math.floor((diff % 86400000) / 3600000);
    const m = Math.floor((diff % 3600000) / 60000);
    const s = Math.floor((diff % 60000) / 1000);
    let label;
    if (d > 0) label = `${d}d ${h}h ${m}m`;
    else if (h > 0) label = `${h}h ${m}m ${s}s`;
    else label = `${m}m ${s}s`;
    return { ended: false, label, d, h, m, s };
}

const TABS = [
    { id: "live", label: "Live Now", icon: Flame },
    { id: "upcoming", label: "Upcoming", icon: Clock },
    { id: "ended", label: "Ended", icon: Gavel },
];

export default function Auctions() {
    const [items, setItems] = useState([]);
    const [tab, setTab] = useState("live");
    const [loading, setLoading] = useState(true);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/auctions", { params: { status: tab } });
            setItems(data || []);
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [tab]);

    return (
        <div className="min-h-screen">
            <section className="bg-navy-950 border-b border-white/5">
                <div className="max-w-7xl mx-auto px-6 lg:px-8 py-16">
                    <div className="flex items-center gap-3 mb-3">
                        <Gavel className="w-5 h-5 text-gold-500" />
                        <span className="label-overline">Online Property Auctions</span>
                    </div>
                    <h1 className="font-heading text-4xl lg:text-6xl font-bold max-w-3xl text-white" data-testid="auctions-page-title">
                        Bank <span className="gold-shimmer">Auction Properties</span> in India
                    </h1>
                    <p className="text-white/65 mt-4 max-w-2xl">SARFAESI, DRT and court-ordered bank auctions across India. Place real-time bids, get verified bank details, EMD info, inspection dates and possession status.</p>

                    <div className="mt-8 flex gap-2 flex-wrap">
                        {TABS.map((t) => {
                            const Icon = t.icon;
                            const active = tab === t.id;
                            return (
                                <button
                                    key={t.id}
                                    data-testid={`auction-tab-${t.id}`}
                                    onClick={() => setTab(t.id)}
                                    className={`px-5 py-2.5 rounded-full font-semibold text-sm flex items-center gap-2 transition-all ${
                                        active ? "bg-gold-500 text-white shadow-lg" : "border border-white/15 text-white/70 hover:border-gold-500/50"
                                    }`}
                                >
                                    <Icon className="w-4 h-4" /> {t.label}
                                </button>
                            );
                        })}
                    </div>
                </div>
            </section>

            <section className="max-w-7xl mx-auto px-6 lg:px-8 py-12">
                {loading ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {[1, 2, 3].map((i) => <div key={i} className="h-80 rounded-2xl shimmer" />)}
                    </div>
                ) : items.length === 0 ? (
                    <div className="text-center py-24 rounded-2xl border border-white/5 bg-navy-800">
                        <Gavel className="w-12 h-12 text-gold-500/60 mx-auto mb-4" />
                        <h3 className="font-heading text-2xl font-bold text-white mb-2">No {tab} auctions</h3>
                        <p className="text-white/60 text-sm">Check back soon or browse other categories.</p>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 stagger" data-testid="auctions-grid">
                        {items.map((a) => <AuctionCard key={a.id} auction={a} />)}
                    </div>
                )}
            </section>
        </div>
    );
}

function AuctionCard({ auction }) {
    const isLive = auction.status === "live";
    const isUpcoming = auction.status === "upcoming";
    const target = isUpcoming ? auction.start_time : auction.end_time;
    const cd = useCountdown(target);
    const img = auction.images?.[0]
        ? fileUrl(auction.images[0])
        : "https://images.unsplash.com/photo-1638454795595-0a0abf68614d?crop=entropy&cs=srgb&fm=jpg&q=85";

    return (
        <Link
            to={`/auctions/${auction.id}`}
            data-testid={`auction-card-${auction.id}`}
            className="group block rounded-2xl overflow-hidden bg-navy-800 border border-white/5 hover:border-gold-500/40 hover-lift transition-colors"
        >
            <div className="relative img-zoom aspect-[16/11]">
                <img src={img} alt={auction.title} className="w-full h-full object-cover" loading="lazy" />
                {isLive && (
                    <div className="absolute top-3 left-3 px-3 py-1 rounded-full bg-red-500 text-white text-[10px] font-bold uppercase tracking-widest flex items-center gap-1.5 animate-pulse">
                        <span className="w-2 h-2 rounded-full bg-white" /> Live Now
                    </div>
                )}
                {isUpcoming && (
                    <div className="absolute top-3 left-3 px-3 py-1 rounded-full bg-gold-500 text-white text-[10px] font-bold uppercase tracking-widest">
                        Upcoming
                    </div>
                )}
                {auction.status === "ended" && (
                    <div className="absolute top-3 left-3 px-3 py-1 rounded-full bg-navy-800 text-white text-[10px] font-bold uppercase tracking-widest">
                        Ended
                    </div>
                )}
                <div className="absolute top-3 right-3 px-3 py-1 rounded-md bg-white/90 backdrop-blur-md font-mono text-xs font-bold flex items-center gap-1.5" style={{ color: "#002657" }}>
                    <Clock className="w-3 h-3 text-gold-500" />
                    {cd.ended ? "Ended" : (isUpcoming ? `Starts in ${cd.label}` : `${cd.label} left`)}
                </div>
            </div>
            <div className="p-5 space-y-3">
                <div className="text-[10px] uppercase tracking-[0.2em] text-gold-500/90 font-semibold flex items-center justify-between">
                    <span>{auction.property_type}{auction.subtype ? ` · ${auction.subtype}` : ""}</span>
                    {auction.auction_type && <span className="text-white/50">{auction.auction_type}</span>}
                </div>
                {/* Borrower name as primary heading (bank-auction listings) */}
                <h3 className="font-heading font-bold text-white text-lg leading-tight line-clamp-2 min-h-[3rem]" data-testid={`auction-borrower-${auction.id}`}>
                    {auction.borrower_name
                        ? <>Borrower: <span className="text-gold-500">{auction.borrower_name}</span></>
                        : auction.title}
                </h3>
                {auction.bank_name && (
                    <div className="text-xs text-white/70 flex items-center gap-1.5">
                        <Building2 className="w-3.5 h-3.5 text-gold-500" />
                        {auction.bank_name}{auction.branch ? ` · ${auction.branch}` : ""}
                    </div>
                )}
                <div className="text-xs text-white/60 flex items-center gap-1">
                    <MapPin className="w-3.5 h-3.5 text-gold-500" />
                    {auction.locality ? `${auction.locality}, ` : ""}{auction.city}
                </div>
                <div className="flex items-center gap-3 pt-3 border-t border-white/5 text-[11px] text-white/70 flex-wrap">
                    {auction.bhk != null && <span className="flex items-center gap-1"><BedDouble className="w-3.5 h-3.5 text-gold-500" /> {auction.bhk} BHK</span>}
                    {auction.area_sqft != null && <span className="flex items-center gap-1"><Maximize className="w-3.5 h-3.5 text-gold-500" /> {auction.area_sqft} sqft</span>}
                    {auction.possession_type && <span className="flex items-center gap-1"><KeyRound className="w-3.5 h-3.5 text-gold-500" /> {auction.possession_type === "physical" ? "Physical" : "Symbolic"}</span>}
                    {auction.emd_amount && <span className="flex items-center gap-1"><IndianRupee className="w-3.5 h-3.5 text-gold-500" /> EMD {formatINR(auction.emd_amount)}</span>}
                </div>
                <div className="flex items-end justify-between pt-3 border-t border-white/5">
                    <div>
                        <div className="text-[10px] uppercase tracking-wider text-white/50">Current Bid</div>
                        <div className="font-heading text-xl font-bold text-gold-500">{formatINR(auction.current_price)}</div>
                    </div>
                    <div className="text-right">
                        <div className="text-[10px] uppercase tracking-wider text-white/50">Bids</div>
                        <div className="font-heading text-xl font-bold text-white flex items-center gap-1 justify-end">
                            <TrendingUp className="w-4 h-4 text-gold-500" /> {auction.bid_count || 0}
                        </div>
                    </div>
                </div>
            </div>
        </Link>
    );
}
